import {defineStore} from "pinia";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {type Ref, ref} from "vue";
import {AthumiService, OperationsActorDto, OperationsActorInfo} from "@/api/enterprise-gateway-api.proxy.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";

export enum SearchOrganisationFilters {
    Kbo = 'kbo',
    Ovo = 'ovo',
    OrgCode = 'org-code',
    SubjectId = 'subject-id',
}

export interface ISearchOrganisationQuery {
    type: SearchOrganisationFilters,
    keyword: string,
}

class OrganisationLoadingState extends LoadingState {
    private readonly _savingEmails: Ref<boolean>;
    private readonly _savingActorDetails: Ref<boolean>;

    constructor() {
        super();
        this._savingEmails = ref(false);
        this._savingActorDetails = ref(false);
    }
    
    get isSavingEmails(): Ref<boolean> {
        return this._savingEmails;
    }

    get isSavingActorDetails(): Ref<boolean> {
        return this._savingActorDetails;
    }
    
    public savingEmails(): void {
        this._savingEmails.value = true;
    }

    public stopSavingEmails(): void {
        this._savingEmails.value = false;
    }

    public savingActorDetails(): void {
        this._savingActorDetails.value = true;
    }

    public stopSavingActorDetails(): void {
        this._savingActorDetails.value = false;
    }
}

export const useOrganisationStore = defineStore('organisation-store', () => {
    const apiProxy = useEnterpriseGatewayApiProxy();
    const errorStore = useErrorStore();
    const loadingState = new OrganisationLoadingState();
    
    const searchOrganisation = async (searchOrganisationQuery: ISearchOrganisationQuery): Promise<OperationsActorDto | undefined> => {
        try {
            loadingState.startLoading();
            switch (searchOrganisationQuery.type) {
                case SearchOrganisationFilters.Kbo: {
                    const result = await apiProxy.operations_GetActorByKbo(searchOrganisationQuery.keyword);
                    loadingState.stopLoading();
                    return result;
                }
                case SearchOrganisationFilters.Ovo: {
                    const result = await apiProxy.operations_GetActorByOvoCode(searchOrganisationQuery.keyword);
                    loadingState.stopLoading();
                    return result;
                }
                case SearchOrganisationFilters.OrgCode: {
                    const result = await apiProxy.operations_GetActorByOrgCode(searchOrganisationQuery.keyword);
                    loadingState.stopLoading();
                    return result;
                }
                case SearchOrganisationFilters.SubjectId: {
                    const result = await apiProxy.operations_GetActorBySubjectId(searchOrganisationQuery.keyword);
                    loadingState.stopLoading();
                    return result;
                }
            }
        } catch (error) {
            loadingState.setError();
            await errorStore.setError(error, true);
            throw error;
        }
    }
    
    const saveEmails = async (actorId: string, athumiService: AthumiService, emails: string[]) => {
        loadingState.savingEmails();
        try {
            await apiProxy.operations_UpdateActorEmailsByActorId(actorId, athumiService, emails);
            loadingState.stopSavingEmails();
        }catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
            throw e;
        }
    }

    const saveActor = async (actorId: string, operationsActorInfo: OperationsActorInfo) => {
        loadingState.savingActorDetails();
        try {
            await apiProxy.operations_UpdateActorByActorId(actorId, operationsActorInfo );
            loadingState.stopSavingActorDetails();
        }catch (e) {
            loadingState.setError();
            await errorStore.setError(e, true);
            throw e;
        }
    }
    
    return {
        isLoading: loadingState.isLoading,
        savingEmails: loadingState.isSavingEmails,
        isSavingActorDetails: loadingState.isSavingActorDetails,
        errorLoading: loadingState.errorLoading,
        searchOrganisation,
        saveEmails,
        saveActor
    };
});